import React, { useState } from 'react';
import { login } from '../api/api';

interface LoginPageProps {
  onClose: () => void;
  onRegisterClick?: () => void;
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onClose, onRegisterClick, onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ identity: email, password });
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-[100] flex flex-col min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
        <div className="flex items-center">
          <img src="/tip24beth.svg" alt="Tip24" className="h-8 object-contain" style={{ filter: 'brightness(0)' }} />
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 transition p-1"
          aria-label="Kapat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 py-8">
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">Hoş Geldiniz</h1>

        <p className="text-center text-sm text-gray-500 mb-6">
          Hesabınız yok mu?{' '}
          <button className="text-blue-600 hover:underline font-medium" onClick={onRegisterClick}>
            Şimdi kayıt olun
          </button>
        </p>

        <p className="text-center text-xs text-gray-400 mb-6">* ile işaretli alanlar zorunludur.</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              * E-Posta / Kullanıcı Adı
            </label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-gray-500 bg-white"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              * Şifre
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-gray-500 bg-white pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-3">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-5 h-5 border-2 border-gray-400 rounded cursor-pointer accent-gray-800"
            />
            <label htmlFor="rememberMe" className="text-sm text-gray-700 cursor-pointer select-none">
              Beni hatırla
            </label>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-600 text-sm text-center -mt-1">{error}</p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white font-semibold py-4 rounded-md text-base hover:bg-gray-700 transition mt-2 disabled:opacity-60"
          >
            {loading ? 'Bitte warten…' : 'Giriş Yap'}
          </button>
        </form>

        {/* Links */}
        <div className="mt-6 text-center">
          <button className="text-blue-600 text-sm hover:underline">
            Şifremi unuttum?
          </button>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          Dükkan müşterisi misiniz?{' '}
          <button className="text-blue-600 hover:underline font-medium">
            Online hesap aktifleştir
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
